import './info-list.scss';

import { Button, Typography } from 'antd';
import classNames from 'classnames';

import { DownloadOutlined } from '@/media/antd-icons';
import { SYMBOLS } from '@/shared/constants';

/** Пара «подпись — значение». Пустое значение показывается длинным тире. */
export interface IInfoListItem {
  label: string;
  value?: string;
  /** Цвет значения: хорошо, внимание, плохо. */
  tone?: 'success' | 'warning' | 'danger';
  /** Значение можно скопировать кнопкой рядом. */
  copyable?: boolean;
  /** Значение — ссылка: телефон (`tel:`) или файл. */
  href?: string;
  /** Ссылка на файл: открывается в новой вкладке, рядом иконка скачивания. */
  isFile?: boolean;
  /** Значение — действие, например «Посмотреть» с открытием модалки. */
  onClick?: () => void;
}

interface IProps {
  /** Колонки пар; на узком контейнере колонки встают друг под другом. */
  columns: IInfoListItem[][];
  /** Наименьшая ширина колонки, px: уже — колонки переносятся. По умолчанию 300. */
  minColumnWidth?: number;
  className?: string;
}

const InfoValue: React.FC<IInfoListItem> = ({
  value,
  tone,
  copyable,
  href,
  isFile,
  onClick
}) => {
  if (!value)
    return <span className="info-list__text">{SYMBOLS.longDash}</span>;

  if (onClick) {
    return (
      <Button type="link" className="info-list__action" onClick={onClick}>
        {value}
      </Button>
    );
  }

  if (href) {
    return (
      <Typography.Link
        className="info-list__text"
        href={href}
        {...(isFile && { target: '_blank', rel: 'noopener noreferrer' })}
        copyable={copyable ? { text: value } : false}
      >
        {value}
        {isFile && <DownloadOutlined className="info-list__file-icon" />}
      </Typography.Link>
    );
  }

  return (
    <Typography.Text
      className="info-list__text"
      strong
      type={tone}
      copyable={copyable}
    >
      {value}
    </Typography.Text>
  );
};

/**
 * Колонки пар «подпись: значение» — сведения карточки клиента, скоринга, договора.
 * На узком контейнере каждая пара — строка с подписью слева и значением справа.
 */
const InfoList: React.FC<IProps> = ({ columns, minColumnWidth, className }) => (
  <div
    className={classNames('info-list', className)}
    style={
      minColumnWidth
        ? ({
            '--info-list-column-min-width': `${minColumnWidth}px`
          } as React.CSSProperties)
        : undefined
    }
  >
    <div className="info-list__columns">
      {columns.map((items, index) => (
        <dl key={index} className="info-list__column">
          {items.map((item) => (
            <div key={item.label} className="info-list__item">
              <dt className="info-list__label">{item.label}:</dt>
              <dd className="info-list__value">
                <InfoValue {...item} />
              </dd>
            </div>
          ))}
        </dl>
      ))}
    </div>
  </div>
);

export default InfoList;
